import {
  bucketDaySeries,
  decimalToCents,
  formatBucket,
  parseIsoDate,
  type SeriesGranularity,
} from "@/features/staff/reports/charts/series"
import type { DayTotal, ForecastHorizonUnit, ForecastPoint } from "@/features/staff/reports/types"

/**
 * One continuous axis, half real and half estimated.
 *
 * The real half is the CURRENT period's own `byDay` rows, bucketed at the
 * forecast's own unit -- `bucketDaySeries(byDay, "week")` produces the same
 * Monday-anchored keys `Staff::StatsRange::Bucket#key` does server-side, which
 * is exactly what lets a forecast week and a historical week share one x-axis
 * without either side inventing its own bucketing rule. The granularity here
 * tracks the HORIZON's unit, never the period's own length: a chart drawn for
 * `day-30` is a daily chart even when the period behind it is a quarter, because
 * the six-week-and-up roll-up `chooseGranularity` applies to `ReportDaySeries` is
 * a different chart answering a different question.
 *
 * The estimated half is `result.points`, already bucketed server-side to the
 * same unit -- `Staff::Forecast::Window` guarantees it.
 *
 * **The seam.** `connectNulls={false}` on both lines is load-bearing (see
 * `forecast-chart.tsx`), which means a solid "actual" line and a dashed
 * "expected" line drawn from two disjoint point sets would leave a gap between
 * them instead of meeting. So the LAST real bucket is given a second value --
 * its own amount, copied into `expected` -- and nothing else changes about it:
 * no band, because a real day never carried an interval to draw one from.
 */

export type ForecastChartPoint = {
  key: string
  label: string
  tick: string
  /** Cents. `null` for a bucket with no close, or for a forecast-only point. */
  actual: number | null
  /** The server's own string, unmodified -- what the frame's table reads. */
  actualAmount: string | null
  /** Cents. `null` for an actual-only point, except at the seam -- see above. */
  expected: number | null
  expectedAmount: string | null
  lowerAmount: string | null
  upperAmount: string | null
  /**
   * The band, as a stacked pair for recharts' "invisible base + visible fill"
   * trick: `lowerBase` (cents) is the invisible area under the lower bound,
   * `bandHeight` (cents) is the visible slice from `lower` to `upper`. Both
   * `null` outside the forecast half, so the band never reaches back over
   * real data.
   */
  lowerBase: number | null
  bandHeight: number | null
}

function granularityOf(unit: ForecastHorizonUnit): SeriesGranularity {
  return unit === "week" ? "week" : "day"
}

export function buildForecastChartSeries(
  byDay: DayTotal[],
  points: ForecastPoint[],
  unit: ForecastHorizonUnit
): ForecastChartPoint[] {
  const granularity = granularityOf(unit)

  const actualRows: ForecastChartPoint[] = bucketDaySeries(byDay, granularity).map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    tick: bucket.tick,
    actual: bucket.value,
    actualAmount: bucket.amount,
    expected: null,
    expectedAmount: null,
    lowerAmount: null,
    upperAmount: null,
    lowerBase: null,
    bandHeight: null,
  }))

  const last = actualRows[actualRows.length - 1]
  if (last && last.actual !== null) {
    last.expected = last.actual
    last.expectedAmount = last.actualAmount
  }

  // A bucket the real half already drew is never drawn a second time as an
  // estimate. The forecast is anchored on `as_of` -- the last close the fit READ,
  // resolved at 5am -- while `byDay` grows the moment tonight's shift closes. A
  // restaurant that closes before midnight therefore spends every evening with
  // `as_of` one day behind the last bucket on screen, which is exactly when an
  // owner opens this report: the same date would arrive twice, once as banked
  // money and once as a guess about it, taking two slots on a categorical axis,
  // two rows under one React key in the frame's table, and an ambiguous seam
  // marker between them. The observation wins; a bucket that already happened is
  // not something the model gets to have an opinion about.
  const actualKeys = new Set(actualRows.map((row) => row.key))

  const forecastRows: ForecastChartPoint[] = points
    .filter((point) => !actualKeys.has(point.date))
    .map((point) => {
      const date = parseIsoDate(point.date)
      const lower = decimalToCents(point.lower)
      const upper = decimalToCents(point.upper)

      return {
        key: point.date,
        label: date ? formatBucket(date, granularity, false) : point.date,
        tick: date ? formatBucket(date, granularity, true) : point.date,
        actual: null,
        actualAmount: null,
        expected: decimalToCents(point.mean),
        expectedAmount: point.mean,
        lowerAmount: point.lower,
        upperAmount: point.upper,
        lowerBase: lower,
        // Clamped at zero: `Location::SalesForecast` already clamps a negative
        // lower bound before it is persisted, but an upper below a lower is not
        // a shape this chart should ever try to render as a negative-height
        // area.
        bandHeight: Math.max(0, upper - lower),
      }
    })

  return [...actualRows, ...forecastRows]
}

/**
 * The horizon rolled into one figure and one band, in cents -- the hero the
 * card leads with.
 *
 * **Means add. Bounds do not.** Summing `lower` and summing `upper` point by
 * point would draw the band you get if every single day in the horizon came in
 * at its worst together, and then print the interval width beside it as though
 * that were an 80% outcome. It is not remotely one: seven independent bad days
 * in a row is a far rarer event than one bad day, and pretending otherwise is
 * how a weekly band ends up several times wider than the week's actual
 * plausible range.
 *
 * So the half-widths combine in quadrature -- `sqrt(sum of squares)` -- which
 * is not a second opinion about the statistics but literally the rule
 * `Staff::Forecast::Adapter#in_quadrature` already applies server-side when it
 * folds seven daily points into one weekly one. Rolling seven `day` points up
 * here now lands on the SAME band the backend would have produced for that week
 * under `week-1`, which is the property that was missing: the two horizons
 * stopped disagreeing with each other about the same seven days.
 *
 * The half-width taken from each point is `upper - mean`, matching the backend
 * exactly. `lower` is the one that gets clamped at zero before storage, so it
 * is the one whose distance from the mean has already been altered; reading the
 * spread off it would feed a truncated width back into the sum.
 */
export function sumForecastCents(points: ForecastPoint[]): {
  mean: number
  lower: number
  upper: number
} {
  const mean = points.reduce((total, point) => total + decimalToCents(point.mean), 0)

  const varianceCents2 = points.reduce((total, point) => {
    const spread = decimalToCents(point.upper) - decimalToCents(point.mean)
    return total + spread * spread
  }, 0)

  const spread = Math.round(Math.sqrt(varianceCents2))

  // Sales have a floor, and the backend clamps each point's own lower bound at
  // zero for the same reason. A band reaching under zero is an artefact of the
  // normal assumption, not a claim about the restaurant.
  return { mean, lower: Math.max(0, mean - spread), upper: mean + spread }
}
